import { useState } from "react";
import { Checkbox } from "../components/ui/checkbox";
import { PROJECT_DIVISIONS, CLIENT_TYPES, GROUP_LABEL, productOptions } from "../lib/projectOptions";

export const EMPTY_PROJECT = { title: "", division: PROJECT_DIVISIONS[0], client_type: "", city: "", province: "", period: "", short: "", description: "", products: [], cover_photo_id: "", consent: false, consent_date: "", consent_note: "" };

// Dal lavoro restituito dal server al corpo del form (PUT richiede tutti i campi)
export const toForm = (p) => (p ? Object.fromEntries(Object.keys(EMPTY_PROJECT).map((k) => [k, p[k] ?? EMPTY_PROJECT[k]])) : EMPTY_PROJECT);

export const inputCls = "mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-[#0B1F3A] outline-none focus:border-[#0B1F3A] disabled:bg-slate-50";
const labelCls = "block text-xs font-bold uppercase tracking-wider text-[#4F5D70]";

const Field = ({ label, children, hint, className = "" }) => (
    <label className={`${labelCls} ${className}`}>
        {label}
        {children}
        {hint && <span className="mt-1 block text-[11px] font-normal normal-case tracking-normal text-[#94A3B8]">{hint}</span>}
    </label>
);

const ClientTypeField = ({ value, onChange }) => {
    const inList = CLIENT_TYPES.includes(value);
    const [custom, setCustom] = useState(!!value && !inList);
    return (
        <Field label="Tipo di cliente">
            <select value={custom ? "__custom" : value} onChange={(e) => { if (e.target.value === "__custom") { setCustom(true); onChange(""); } else { setCustom(false); onChange(e.target.value); } }} className={inputCls} data-testid="project-client-type">
                <option value="">— Seleziona —</option>
                {CLIENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                <option value="__custom">Altro valore (scrivi tu)…</option>
            </select>
            {custom && <input value={value} onChange={(e) => onChange(e.target.value)} placeholder="Es. Scuola guida" maxLength={80} className={inputCls} data-testid="project-client-type-custom" />}
        </Field>
    );
};

const ProductsPicker = ({ selected, options, onChange }) => {
    const toggle = (opt) => onChange(selected.some((s) => s.key === opt.key) ? selected.filter((s) => s.key !== opt.key) : [...selected, opt]);
    return (
        <div data-testid="project-products">
            <span className={labelCls}>Prodotti installati <span className="font-normal normal-case tracking-normal">(anche più di uno)</span></span>
            <p className="mt-1 text-[11px] text-[#94A3B8]">La pagina del lavoro rimanda alle schede prodotto scelte e ogni scheda mostra i lavori collegati in &quot;Installati da noi&quot;.</p>
            {["christ", "officina"].map((g) => (
                <div key={g} className="mt-3">
                    <div className="text-[11px] font-bold text-[#0B1F3A]">{GROUP_LABEL[g]}</div>
                    <div className="mt-1.5 flex flex-wrap gap-2">
                        {options.filter((o) => o.group === g).map((o) => {
                            const on = selected.some((s) => s.key === o.key);
                            return (
                                <button key={o.key} type="button" onClick={() => toggle(o)} aria-pressed={on} data-testid={`project-product-${o.key}`} className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors ${on ? "border-[#0B1F3A] bg-[#0B1F3A] text-white" : "border-slate-200 bg-white text-[#0B1F3A] hover:border-[#0B1F3A]"}`}>
                                    {o.name}
                                </button>
                            );
                        })}
                    </div>
                </div>
            ))}
        </div>
    );
};

const ConsentBlock = ({ form, set }) => (
    <div className={`rounded-2xl border p-4 ${form.consent ? "border-emerald-200 bg-emerald-50/60" : "border-amber-200 bg-amber-50/60"}`} data-testid="project-consent-block">
        <label className="flex cursor-pointer items-start gap-3">
            <Checkbox checked={form.consent} onCheckedChange={(v) => set("consent", !!v)} data-testid="project-consent" className="mt-0.5" />
            <span className="text-sm text-[#0B1F3A]">
                <span className="font-bold">Il cliente ha dato il consenso alla pubblicazione delle foto</span>
                <span className="mt-0.5 block text-xs text-[#4F5D70]">Obbligatorio per pubblicare. Data e nota restano visibili solo nel pannello.</span>
            </span>
        </label>
        <div className="mt-3 grid gap-3 sm:grid-cols-[180px_1fr]">
            <Field label="Data consenso"><input type="date" value={form.consent_date} onChange={(e) => set("consent_date", e.target.value)} className={inputCls} data-testid="project-consent-date" /></Field>
            <Field label="Nota (es. consenso scritto firmato)"><input value={form.consent_note} onChange={(e) => set("consent_note", e.target.value)} maxLength={300} placeholder="Es. consenso scritto firmato il giorno della consegna" className={inputCls} data-testid="project-consent-note" /></Field>
        </div>
    </div>
);

export const AdminProjectForm = ({ form, setForm, products, onSubmit, busy, isNew }) => {
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const options = productOptions(products);
    return (
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }} className="space-y-5" data-testid="project-form">
            <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Titolo del lavoro *" className="sm:col-span-2"><input required minLength={3} maxLength={120} value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Es. Portale CADIS per concessionaria a Cosenza" className={inputCls} data-testid="project-title" /></Field>
                <Field label="Divisione">
                    <select value={form.division} onChange={(e) => set("division", e.target.value)} className={inputCls} data-testid="project-division">
                        {PROJECT_DIVISIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                </Field>
                <ClientTypeField value={form.client_type} onChange={(v) => set("client_type", v)} />
                <Field label="Comune"><input value={form.city} onChange={(e) => set("city", e.target.value)} maxLength={80} placeholder="Es. Rende" className={inputCls} data-testid="project-city" /></Field>
                <div className="grid grid-cols-2 gap-4">
                    <Field label="Provincia"><input value={form.province} onChange={(e) => set("province", e.target.value.toUpperCase().slice(0, 4))} placeholder="CS" className={inputCls} data-testid="project-province" /></Field>
                    <Field label="Mese / anno"><input type="month" value={form.period} onChange={(e) => set("period", e.target.value)} className={inputCls} data-testid="project-period" /></Field>
                </div>
                <Field label="Descrizione breve" className="sm:col-span-2" hint="1-2 frasi: compare nella scheda in home e come descrizione per Google (max 600 caratteri)."><textarea rows={2} maxLength={600} value={form.short} onChange={(e) => set("short", e.target.value)} className={inputCls} data-testid="project-short" /></Field>
                <Field label="Descrizione completa" className="sm:col-span-2" hint="Facoltativa. Vai a capo per separare i paragrafi."><textarea rows={5} maxLength={4000} value={form.description} onChange={(e) => set("description", e.target.value)} className={inputCls} data-testid="project-description" /></Field>
            </div>
            <ProductsPicker selected={form.products} options={options} onChange={(v) => set("products", v)} />
            <ConsentBlock form={form} set={set} />
            <button type="submit" disabled={busy} data-testid="project-save" className="rounded-full bg-[#0B1F3A] px-7 py-3.5 text-sm font-bold text-white hover:bg-[#132b4f] disabled:opacity-60">
                {busy ? "Salvataggio…" : isNew ? "Crea il lavoro e passa alle foto" : "Salva modifiche"}
            </button>
        </form>
    );
};
